import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, CircularProgress, Dialog, Grow, IconButton, TextField } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import DirectionsRunRoundedIcon from "@mui/icons-material/DirectionsRunRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import axios from "axios";
import API_BASE_URL from "../config/appConfig";
import "./modal.scss";

const SmoothGrow = React.forwardRef(function SmoothGrow(props, ref) {
  return <Grow ref={ref} timeout={{ enter: 320, exit: 200 }} {...props} />;
});

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("accessToken")}` });

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/* The API answers with { message }; a server without these routes answers
   with an HTML 404 page, which is not worth showing as-is. */
const messageOf = (error, fallback) => {
  const data = error?.response?.data;
  if (data && typeof data === "object" && data.message) return data.message;
  if (error?.response?.status === 404) return "Project delete is not available on this server yet.";
  return fallback;
};

/**
 * Permanent delete of an inactive project. Before anything can be confirmed it
 * loads what the delete takes with it — tasks, active sprints, people — and the
 * owner has to type the project name, because there is no undo.
 */
const DeleteProjectModal = ({ open, projectData, onClose, onDeleted }) => {
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!open || !projectData?._id) return undefined;
    let cancelled = false;
    setSummary(null);
    setLoadError("");
    setConfirmText("");
    setDeleteError("");

    axios
      .get(`${API_BASE_URL}/projects/deleteSummary/${projectData._id}`, { headers: authHeaders() })
      .then(({ data }) => {
        if (!cancelled) setSummary(data);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(messageOf(error, "Could not load the project details"));
      });

    return () => {
      cancelled = true;
    };
  }, [open, projectData?._id]);

  const nameMatches = confirmText.trim() === String(projectData?.title || "").trim();
  const canDelete = Boolean(summary) && !summary.active && nameMatches && !deleting;

  const handleDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await axios.delete(`${API_BASE_URL}/projects/deleteProject/${projectData._id}`, { headers: authHeaders() });
      onDeleted(projectData);
    } catch (error) {
      setDeleteError(messageOf(error, "Could not delete the project. Please try again."));
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    if (!deleting) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="delete-project-dialog-title"
      maxWidth="xs"
      fullWidth
      TransitionComponent={SmoothGrow}
      PaperProps={{ className: "pj-modal pj-modal--danger pj-modal--confirm pj-modal--delete" }}
      slotProps={{ backdrop: { className: "pj-modal-backdrop" } }}
    >
      <div className="pj-modal-body">
        <IconButton className="pj-modal-close" size="small" onClick={handleClose} aria-label="Close" disabled={deleting}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <span className="pj-modal-badge">
          <DeleteForeverRoundedIcon />
        </span>

        <h3 className="pj-modal-title" id="delete-project-dialog-title">
          Delete project permanently
        </h3>
        <p className="pj-modal-sub">
          Everything in this project — its tasks, sprints, comments and history — is removed for every member. This
          cannot be undone.
        </p>

        <div className="pj-modal-target">
          <span className="pj-modal-target-label">Project</span>
          <span className="pj-modal-target-name">
            {projectData?.title}
            {projectData?.key ? ` · ${projectData.key}` : ""}
          </span>
        </div>

        {!summary && !loadError ? (
          <div className="pj-delete-loading">
            <CircularProgress size={22} />
          </div>
        ) : null}

        {loadError ? <p className="pj-delete-error">{loadError}</p> : null}

        {summary ? (
          <>
            <div className="pj-delete-stats">
              <div className="pj-stat is-tasks">
                <span className="pj-stat-icon"><AssignmentRoundedIcon /></span>
                <div>
                  <strong>{summary.tasks.total}</strong>
                  <span>Tasks</span>
                </div>
              </div>
              <div className={`pj-stat ${summary.sprints.active ? "is-open" : "is-sprints"}`}>
                <span className="pj-stat-icon"><DirectionsRunRoundedIcon /></span>
                <div>
                  <strong>{summary.sprints.active}</strong>
                  <span>Active sprints</span>
                </div>
              </div>
              <div className="pj-stat is-users">
                <span className="pj-stat-icon"><GroupRoundedIcon /></span>
                <div>
                  <strong>{summary.users}</strong>
                  <span>Users</span>
                </div>
              </div>
            </div>

            <ul className="pj-delete-notes">
              {summary.tasks.open ? <li>{plural(summary.tasks.open, "task")} still open</li> : null}
              {summary.sprints.active ? (
                <li>Running now: {summary.sprints.activeNames.join(", ")}</li>
              ) : null}
              {summary.sprints.total ? <li>{plural(summary.sprints.total, "sprint")} in total, including completed</li> : null}
              <li>{plural(summary.users, "user")} will lose access</li>
            </ul>

            {summary.active ? (
              <p className="pj-delete-error">Deactivate the project before deleting it.</p>
            ) : (
              <div className="pj-field-group">
                <label className="pj-field-label" htmlFor="delete-project-confirm">
                  Type <strong>{projectData?.title}</strong> to confirm
                </label>
                <TextField
                  id="delete-project-confirm"
                  size="small"
                  fullWidth
                  autoComplete="off"
                  value={confirmText}
                  onChange={(event) => setConfirmText(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") handleDelete();
                  }}
                  disabled={deleting}
                />
              </div>
            )}

            {deleteError ? <p className="pj-delete-error">{deleteError}</p> : null}
          </>
        ) : null}
      </div>

      <div className="pj-modal-actions">
        <Button className="pj-btn pj-btn--quiet" onClick={handleClose} disabled={deleting}>
          Cancel
        </Button>
        <Button className="pj-btn pj-btn--primary" onClick={handleDelete} disabled={!canDelete}>
          {deleting ? "Deleting…" : "Delete project"}
        </Button>
      </div>
    </Dialog>
  );
};

DeleteProjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  projectData: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
};

export default DeleteProjectModal;
